import { Box, Fab, Grid, IconButton, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { FieldArray } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Center } from "../../../../components/center";
import { Form } from "../../../../components/form";
import { FormButton } from "../../../../components/form_button";
import { SelectInput } from "../../../../components/select_input";
import { TextInput } from "../../../../components/text_input";
import { IAsyncData, IFormProps } from "../../../../core/models";
import { isPending } from "../../../../core/redux";
import { fixAmount } from "../../../../core/utils";
import { IAppReduxState } from "../../../../redux/store";
import { IClient } from "../../../client/data/entities";
import { clientRedux } from "../../../client/ui/state/state";
import { IExpense } from "../../../expense/data/entities";
import { expenseRedux } from "../../../expense/ui/state/state";
import { IProduct } from "../../../product/data/entities";
import { productRedux } from "../../../product/ui/state/state";
import { calcCleanWeight, calcWastePercent } from "../../../product/utils";
import { ETransactionType } from "../../data/models";
import {
  IExpenseTransactionForm,
  IProductBuyBulkTransactionForm,
  productBuyTransactionFormValidation,
} from "../../data/product_buy_transaction_entity";
import { IProductTransactionForm } from "../../data/product_buy_transaction_entity";
import { calcTransactionFormSummary } from "../../utils";

interface IProps extends IFormProps<IProductBuyBulkTransactionForm> {}

export const ProductBuyTransactionForm: React.FC<IProps> = (props: IProps) => {
  const { branch, submitTitle } = props;
  const loading = isPending(branch);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clientRedux.actions.getList());
    dispatch(productRedux.actions.getList());
    dispatch(expenseRedux.actions.getList());
  }, [dispatch]);
  const clientListBranch = useSelector<IAppReduxState, IAsyncData<IClient[]>>((state) => state.client.list);
  const productListBranch = useSelector<IAppReduxState, IAsyncData<IProduct[]>>((state) => state.product.list);
  const expenseListBranch = useSelector<IAppReduxState, IAsyncData<IExpense[]>>((state) => state.expense.list);

  const initialProductTransactionValues: IProductTransactionForm = {
    productId: "",
    amount: 0,
    dirtyWeight: 0,
    wastePercent: 0,
    cleanWeight: 0,
    pricePerKg: 0,
  };

  const initialExpenseTransactionValues: IExpenseTransactionForm = {
    expenseId: "",
    amount: 0,
  };

  return (
    <Form<IProductBuyBulkTransactionForm>
      {...props}
      initialValues={{
        clientId: "",
        productTransactions: [initialProductTransactionValues],
        expenseTransactions: [],
        type: ETransactionType.PRODUCT_BUY,
      }}
      validationSchema={productBuyTransactionFormValidation}
    >
      {({ values, setFieldValue }) => {
        const total = calcTransactionFormSummary([...values.productTransactions, ...values.expenseTransactions]);

        return (
          <>
            <Grid container justify="center">
              <Typography variant="h5">Alış</Typography>
            </Grid>
            <Grid container>
              <Grid item sm={3}>
                <SelectInput<IClient>
                  value={values.clientId}
                  options={clientListBranch}
                  name={`clientId`}
                  label="Klient"
                  renderLabel={(a) => a.name}
                />
              </Grid>
            </Grid>

            <Typography>Mallar</Typography>
            <FieldArray name="productTransactions">
              {(arrayHelpers) => {
                return (
                  <>
                    {values.productTransactions.map((productTransaction, index) => {
                      return (
                        <Grid container key={index} spacing={1}>
                          <Grid item sm={2}>
                            <SelectInput<IProduct>
                              value={productTransaction.productId}
                              options={productListBranch}
                              name={`productTransactions.${index}.productId`}
                              label="Mal"
                              renderLabel={(a) => a.name}
                              onChange={(e) => {
                                const selectedProduct = productListBranch.data?.find(
                                  (product) => product.id === e.target.value,
                                );
                                if (selectedProduct) {
                                  setFieldValue(
                                    `productTransactions.${index}.wastePercent` as any,
                                    selectedProduct?.defaultWastePercent,
                                  );
                                }
                              }}
                            />
                          </Grid>

                          <Grid item sm={2}>
                            <TextInput
                              label="Çirkli çəki"
                              type="number"
                              name={`productTransactions.${index}.dirtyWeight`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setFieldValue(
                                  `productTransactions.${index}.cleanWeight` as any,
                                  calcCleanWeight({
                                    dirtyWeight: val,
                                    wastePercent: values.productTransactions[index].wastePercent,
                                  }),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item sm={2}>
                            <TextInput
                              label="Təmiz çəki"
                              type="number"
                              name={`productTransactions.${index}.cleanWeight`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setFieldValue(
                                  `productTransactions.${index}.wastePercent` as any,
                                  calcWastePercent({
                                    cleanWeight: val,
                                    dirtyWeight: values.productTransactions[index].dirtyWeight,
                                  }),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item sm={1}>
                            <TextInput
                              label="İtki faizi"
                              type="number"
                              name={`productTransactions.${index}.wastePercent`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setFieldValue(
                                  `productTransactions.${index}.cleanWeight` as any,
                                  calcCleanWeight({
                                    wastePercent: val,
                                    dirtyWeight: values.productTransactions[index].dirtyWeight,
                                  }),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item sm={2}>
                            <TextInput
                              label="Cəm qiymət"
                              type="number"
                              name={`productTransactions.${index}.amount`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);

                                setFieldValue(
                                  `productTransactions.${index}.pricePerKg` as any,
                                  fixAmount((val || 0) / values.productTransactions[index].cleanWeight),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item sm={2}>
                            <TextInput
                              label="Qiymət"
                              type="number"
                              name={`productTransactions.${index}.pricePerKg`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);

                                setFieldValue(
                                  `productTransactions.${index}.amount` as any,
                                  fixAmount(values.productTransactions[index].cleanWeight * (val || 0)),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item xs={1}>
                            <Center>
                              <IconButton aria-label="delete" onClick={() => arrayHelpers.remove(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </Center>
                          </Grid>
                        </Grid>
                      );
                    })}

                    <Grid container>
                      <Box p={3}>
                        <Fab
                          size="small"
                          color="secondary"
                          onClick={() => arrayHelpers.push(initialProductTransactionValues)}
                        >
                          <AddIcon />
                        </Fab>
                      </Box>
                    </Grid>
                  </>
                );
              }}
            </FieldArray>

            <Typography>Əlavə xərclər</Typography>
            <FieldArray name="expenseTransactions">
              {(arrayHelpers) => {
                return (
                  <>
                    {values.expenseTransactions.map((expenseTransaction, index) => {
                      return (
                        <Grid container key={index} spacing={1}>
                          <Grid item sm={2}>
                            <SelectInput<IExpense>
                              value={expenseTransaction.expenseId}
                              options={expenseListBranch}
                              name={`expenseTransactions.${index}.expenseId`}
                              label="Əlavə xərc"
                              renderLabel={(a) => a.name}
                            />
                          </Grid>

                          <Grid item sm={2}>
                            <TextInput label="Qiymət" type="number" name={`expenseTransactions.${index}.amount`} />
                          </Grid>

                          <Grid item xs={1}>
                            <Center>
                              <IconButton aria-label="delete" onClick={() => arrayHelpers.remove(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </Center>
                          </Grid>
                        </Grid>
                      );
                    })}

                    <Grid container>
                      <Box p={3}>
                        <Fab
                          size="small"
                          color="secondary"
                          onClick={() => arrayHelpers.push(initialExpenseTransactionValues)}
                        >
                          <AddIcon />
                        </Fab>
                      </Box>
                    </Grid>
                  </>
                );
              }}
            </FieldArray>

            <Box mb={2}>
              <Grid container justify="flex-end">
                <Grid item>
                  <Typography variant="h5" align="right">
                    Cəm: {total} AZN
                  </Typography>
                </Grid>
                <Grid item sm={1}></Grid>
              </Grid>
            </Box>

            <Grid container>
              <Grid item sm={12} md={3}>
                <FormButton label={submitTitle} loading={loading} />
              </Grid>
            </Grid>
          </>
        );
      }}
    </Form>
  );
};
